import { memo } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { green, red } from '../../assets/colors'
import { formatOiQuantity } from '../../util/screener'

const FOOTER_RANGE_INFO = 'Total includes only strikes visible on the chart'

export type OiAnalysisFooterProps = {
    isOIChangeScreen: boolean
    footerDateLabel: string
    totalCallOi: number
    totalPutOi: number
    callOiChange: number
    putOiChange: number
    compact?: boolean
}

export const oiMetricLabelSx = {
    fontSize: 10,
    lineHeight: 1,
    color: 'text.tertiary',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
} as const

const FooterMetric = memo(({ label, value, signed }: { label: string; value: number; signed?: boolean }) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.35, flexWrap: 'wrap' }}>
        <Typography variant="caption" component="span" sx={oiMetricLabelSx}>
            {label}
        </Typography>
        <Tooltip title={FOOTER_RANGE_INFO}>
            <InfoOutlinedIcon
                sx={{ fontSize: '0.75rem', color: 'text.tertiary', cursor: 'help', display: 'block' }}
                aria-label={FOOTER_RANGE_INFO}
            />
        </Tooltip>
        <Typography
            variant="body2"
            component="span"
            fontWeight={500}
            sx={{ lineHeight: 1 }}
            color={signed ? (value >= 0 ? green : red) : undefined}
        >
            {formatOiQuantity(value, signed)}
        </Typography>
    </Box>
))

FooterMetric.displayName = 'FooterMetric'

const OiAnalysisFooter = ({
    isOIChangeScreen,
    footerDateLabel,
    totalCallOi,
    totalPutOi,
    callOiChange,
    putOiChange,
    compact = false,
}: OiAnalysisFooterProps) => (
    <Box
        sx={{
            px: compact ? 1 : 2,
            mb: compact ? 1 : 2,
            display: 'flex',
            justifyContent: 'center',
            gap: compact ? 1.5 : 3,
            alignItems: 'flex-end',
            flexWrap: 'wrap',
        }}
    >
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
            {isOIChangeScreen ? 'OI Change on ' : 'Open Interest on '}
            <Box component="span" sx={{ fontWeight: 500 }}>
                {footerDateLabel}
            </Box>
        </Typography>
        {isOIChangeScreen ? (
            <>
                <FooterMetric label="Total Call OI change" value={callOiChange} signed />
                <FooterMetric label="Total Put OI change" value={putOiChange} signed />
            </>
        ) : (
            <>
                <FooterMetric label="Total Call OI" value={totalCallOi} />
                <FooterMetric label="Total Put OI" value={totalPutOi} />
            </>
        )}
    </Box>
)

export default memo(OiAnalysisFooter)
